/**
 * Alternative Teams Environment Detection Methods
 * No Teams SDK initialization required - Azure deployment optimized
 * Implements Azure best practices with multiple detection strategies
 */

/**
 * Method 1: Window and Frame Analysis
 * Most reliable method for detecting Teams iframe environment
 */
export const detectTeamsViaWindowAnalysis = () => {
  try {
    const indicators = {
      // Primary iframe detection
      isInIframe: window.parent !== window.self,

      // Teams-specific window names
      isTeamsContainer: window.name === "embedded-page-container",
      isTeamsExtension: window.name === "extension-tab-frame",

      // Teams native interface detection
      hasNativeInterface: typeof window.nativeInterface !== "undefined",

      // Frame element analysis
      hasTeamsFrameElement:
        window.frameElement &&
        window.frameElement.src &&
        window.frameElement.src.includes("teams.microsoft.com"),

      // Parent window analysis (safe check)
      parentOriginTeams: (() => {
        try {
          return window.parent.location.hostname.includes(
            "teams.microsoft.com"
          );
        } catch (e) {
          // Cross-origin restriction - likely in Teams iframe
          return window.parent !== window.self;
        }
      })(),

      // Window features analysis
      hasTeamsWindowFeatures:
        window.outerHeight === 0 || window.outerWidth - window.innerWidth < 10,
    };

    const teamsScore = Object.values(indicators).filter(Boolean).length;
    const isTeams = teamsScore >= 2; // Require at least 2 indicators

    console.log("🔍 Window Analysis Detection:", {
      indicators,
      teamsScore,
      isTeams,
      confidence: teamsScore >= 3 ? "high" : teamsScore >= 2 ? "medium" : "low",
    });

    return {
      isTeams,
      method: "window_analysis",
      confidence: teamsScore >= 3 ? "high" : teamsScore >= 2 ? "medium" : "low",
      details: indicators,
      score: teamsScore,
    };
  } catch (error) {
    console.warn("⚠️ Window analysis detection failed:", error);
    return {
      isTeams: false,
      method: "window_analysis_failed",
      confidence: "none",
      error: error.message,
    };
  }
};

/**
 * Method 2: User Agent and Browser Environment Analysis
 */
export const detectTeamsViaUserAgent = () => {
  try {
    const userAgent = navigator.userAgent.toLowerCase();
    const platform = navigator.platform.toLowerCase();

    const indicators = {
      // Teams-specific user agent strings
      hasTeamsUA: userAgent.includes("teams"),
      hasSkypeUA: userAgent.includes("skypeforBusiness"),
      hasElectronUA: userAgent.includes("electron"), // Teams desktop uses Electron

      // Teams-specific platform indicators
      isTeamsPlatform: platform.includes("teams"),

      // Browser environment analysis
      hasTeamsBrowser:
        userAgent.includes("teamswebview") ||
        userAgent.includes("teamsmobileapp"),

      // Vendor and app name detection
      hasTeamsVendor:
        navigator.vendor &&
        navigator.vendor.toLowerCase().includes("microsoft"),

      // App version detection for Teams
      hasTeamsVersion:
        navigator.appVersion &&
        navigator.appVersion.toLowerCase().includes("teams"),
    };

    const teamsScore = Object.values(indicators).filter(Boolean).length;
    const isTeams = teamsScore >= 1; // More lenient for user agent

    console.log("🔍 User Agent Detection:", {
      userAgent: navigator.userAgent,
      indicators,
      teamsScore,
      isTeams,
    });

    return {
      isTeams,
      method: "user_agent",
      confidence: teamsScore >= 2 ? "high" : teamsScore >= 1 ? "medium" : "low",
      details: indicators,
      score: teamsScore,
    };
  } catch (error) {
    console.warn("⚠️ User agent detection failed:", error);
    return {
      isTeams: false,
      method: "user_agent_failed",
      confidence: "none",
      error: error.message,
    };
  }
};

/**
 * Method 3: Document and Location Analysis
 */
export const detectTeamsViaDocument = () => {
  try {
    const indicators = {
      // Referrer analysis
      hasTeamsReferrer:
        document.referrer.includes("teams.microsoft.com") ||
        document.referrer.includes("teams.live.com"),

      // Domain analysis
      hasTeamsDomain: window.location.hostname.includes("teams.microsoft.com"),

      // URL parameter analysis
      hasTeamsParams:
        window.location.search.includes("tokenId") ||
        window.location.search.includes("theme=") ||
        window.location.search.includes("locale=") ||
        window.location.search.includes("tid=") ||
        window.location.search.includes("upn="),

      // Hash analysis for Teams-specific fragments
      hasTeamsHash:
        window.location.hash.includes("teams") ||
        window.location.hash.includes("microsoft"),

      // Document title analysis
      hasTeamsTitle:
        document.title.toLowerCase().includes("teams") ||
        document.title.toLowerCase().includes("microsoft"),

      // Base URI analysis
      hasTeamsBase:
        document.baseURI && document.baseURI.includes("teams.microsoft.com"),
    };

    const teamsScore = Object.values(indicators).filter(Boolean).length;
    const isTeams = teamsScore >= 1;

    console.log("🔍 Document Analysis Detection:", {
      referrer: document.referrer,
      hostname: window.location.hostname,
      search: window.location.search,
      indicators,
      teamsScore,
      isTeams,
    });

    return {
      isTeams,
      method: "document_analysis",
      confidence: teamsScore >= 2 ? "high" : teamsScore >= 1 ? "medium" : "low",
      details: indicators,
      score: teamsScore,
    };
  } catch (error) {
    console.warn("⚠️ Document analysis detection failed:", error);
    return {
      isTeams: false,
      method: "document_analysis_failed",
      confidence: "none",
      error: error.message,
    };
  }
};

/**
 * Method 4: Storage and Context Analysis
 */
export const detectTeamsViaStorage = () => {
  try {
    const indicators = {
      // Local storage analysis
      hasTeamsLocalStorage:
        localStorage.getItem("teamsEnvironment") === "true" ||
        localStorage.getItem("isTeams") === "true" ||
        localStorage.getItem("microsoftTeams") !== null,

      // Session storage analysis
      hasTeamsSessionStorage:
        sessionStorage.getItem("teamsContext") === "true" ||
        sessionStorage.getItem("teamsEnvironmentDetected") === "true" ||
        sessionStorage.getItem("azureTeamsDetected") === "true",

      // Cookie analysis
      hasTeamsCookies:
        document.cookie.includes("teams") ||
        document.cookie.includes("skype") ||
        document.cookie.includes("microsoft"),

      // Previous detection cache
      hasCachedDetection:
        sessionStorage.getItem("azureTeamsDetectionCache") !== null,

      // Teams-specific storage keys
      hasTeamsKeys:
        Object.keys(localStorage).some(
          (key) =>
            key.toLowerCase().includes("teams") ||
            key.toLowerCase().includes("microsoft")
        ) ||
        Object.keys(sessionStorage).some(
          (key) =>
            key.toLowerCase().includes("teams") ||
            key.toLowerCase().includes("microsoft")
        ),
    };

    const teamsScore = Object.values(indicators).filter(Boolean).length;
    const isTeams = teamsScore >= 1;

    console.log("🔍 Storage Analysis Detection:", {
      indicators,
      teamsScore,
      isTeams,
      localStorageKeys: Object.keys(localStorage),
      sessionStorageKeys: Object.keys(sessionStorage),
    });

    return {
      isTeams,
      method: "storage_analysis",
      confidence: teamsScore >= 2 ? "high" : teamsScore >= 1 ? "medium" : "low",
      details: indicators,
      score: teamsScore,
    };
  } catch (error) {
    console.warn("⚠️ Storage analysis detection failed:", error);
    return {
      isTeams: false,
      method: "storage_analysis_failed",
      confidence: "none",
      error: error.message,
    };
  }
};

/**
 * Method 5: Global Objects and API Analysis
 */
export const detectTeamsViaGlobalObjects = () => {
  try {
    const indicators = {
      // Microsoft Teams global objects
      hasMicrosoftTeams: typeof window.microsoftTeams !== "undefined",

      // Teams-specific global variables
      hasTeamsGlobals:
        typeof window.nativeInterface !== "undefined" ||
        typeof window.chrome?.webview !== "undefined",

      // Office/Teams integration objects
      hasOfficeObjects:
        typeof window.Office !== "undefined" ||
        typeof window.OSF !== "undefined",

      // Teams authentication objects
      hasTeamsAuth:
        typeof window.acquireToken !== "undefined" ||
        typeof window.getAccessToken !== "undefined",

      // Electron detection (Teams desktop)
      hasElectron:
        typeof window.process !== "undefined" &&
        window.process.type === "renderer",

      // WebView detection (Teams mobile)
      hasWebView:
        typeof window.webkit?.messageHandlers !== "undefined" ||
        typeof window.external?.notify !== "undefined",

      // Teams-specific events or handlers
      hasTeamsHandlers:
        typeof window.onTeamsThemeChanged !== "undefined" ||
        typeof window.microsoftTeamsLib !== "undefined",
    };

    const teamsScore = Object.values(indicators).filter(Boolean).length;
    const isTeams = teamsScore >= 1;

    console.log("🔍 Global Objects Detection:", {
      indicators,
      teamsScore,
      isTeams,
      globalObjects: {
        microsoftTeams: typeof window.microsoftTeams,
        nativeInterface: typeof window.nativeInterface,
        Office: typeof window.Office,
        chrome: typeof window.chrome,
      },
    });

    return {
      isTeams,
      method: "global_objects",
      confidence: teamsScore >= 2 ? "high" : teamsScore >= 1 ? "medium" : "low",
      details: indicators,
      score: teamsScore,
    };
  } catch (error) {
    console.warn("⚠️ Global objects detection failed:", error);
    return {
      isTeams: false,
      method: "global_objects_failed",
      confidence: "none",
      error: error.message,
    };
  }
};

/**
 * Method 6: Network and Performance Analysis
 */
export const detectTeamsViaNetwork = () => {
  try {
    const indicators = {
      // Connection analysis
      hasTeamsConnection:
        navigator.connection &&
        navigator.connection.effectiveType &&
        window.parent !== window.self, // Teams typically loads in controlled network

      // Performance timing analysis
      hasTeamsPerformance:
        performance.navigation &&
        performance.navigation.type === 0 && // Navigated to (not refreshed)
        window.parent !== window.self,

      // Service worker detection (Teams PWA)
      hasTeamsServiceWorker:
        "serviceWorker" in navigator &&
        navigator.serviceWorker.controller &&
        navigator.serviceWorker.controller.scriptURL.includes("teams"),

      // Network information
      hasTeamsNetworkInfo:
        navigator.onLine &&
        window.parent !== window.self &&
        window.location.protocol === "https:", // Teams requires HTTPS

      // Memory usage patterns (Teams is memory intensive)
      hasTeamsMemoryPattern:
        performance.memory &&
        performance.memory.usedJSHeapSize > 10000000 && // > 10MB
        window.parent !== window.self,
    };

    const teamsScore = Object.values(indicators).filter(Boolean).length;
    const isTeams = teamsScore >= 2; // Network indicators need more confirmation

    console.log("🔍 Network Analysis Detection:", {
      indicators,
      teamsScore,
      isTeams,
      connection: navigator.connection,
      performance: {
        memory: performance.memory,
        navigation: performance.navigation,
      },
    });

    return {
      isTeams,
      method: "network_analysis",
      confidence:
        teamsScore >= 3 ? "medium" : teamsScore >= 2 ? "low" : "very_low",
      details: indicators,
      score: teamsScore,
    };
  } catch (error) {
    console.warn("⚠️ Network analysis detection failed:", error);
    return {
      isTeams: false,
      method: "network_analysis_failed",
      confidence: "none",
      error: error.message,
    };
  }
};

/**
 * Comprehensive Alternative Teams Detection
 * Combines all methods for maximum reliability without SDK initialization
 */
export const detectTeamsEnvironmentAlternative = () => {
  console.log(
    "🎯 Starting comprehensive alternative Teams detection (No SDK)..."
  );

  try {
    // Run all detection methods
    const detectionResults = [
      detectTeamsViaWindowAnalysis(),
      detectTeamsViaUserAgent(),
      detectTeamsViaDocument(),
      detectTeamsViaStorage(),
      detectTeamsViaGlobalObjects(),
      detectTeamsViaNetwork(),
    ];

    // Calculate overall confidence
    const teamsDetections = detectionResults.filter((result) => result.isTeams);
    const totalScore = detectionResults.reduce(
      (sum, result) => sum + (result.score || 0),
      0
    );
    const avgConfidence = teamsDetections.length / detectionResults.length;

    const isTeams = teamsDetections.length >= 2; // Require at least 2 methods to agree

    let overallConfidence = "low";
    if (avgConfidence >= 0.6 && totalScore >= 8) {
      overallConfidence = "very_high";
    } else if (avgConfidence >= 0.5 && totalScore >= 6) {
      overallConfidence = "high";
    } else if (avgConfidence >= 0.4 && totalScore >= 4) {
      overallConfidence = "medium";
    } else if (avgConfidence >= 0.3 && totalScore >= 2) {
      overallConfidence = "low";
    } else {
      overallConfidence = "very_low";
    }

    const result = {
      isTeams,
      method: "comprehensive_alternative",
      confidence: overallConfidence,
      detectionResults,
      totalScore,
      avgConfidence,
      agreementCount: teamsDetections.length,
      timestamp: Date.now(),
    };

    console.log("✅ Comprehensive alternative detection completed:", {
      isTeams,
      confidence: overallConfidence,
      agreementCount: teamsDetections.length,
      totalScore,
      methods: detectionResults.map((r) => ({
        method: r.method,
        isTeams: r.isTeams,
        confidence: r.confidence,
      })),
    });

    // Cache the result
    if (isTeams) {
      sessionStorage.setItem(
        "alternativeTeamsDetection",
        JSON.stringify(result)
      );
      localStorage.setItem("teamsEnvironment", "true");
    }

    return result;
  } catch (error) {
    console.error("❌ Comprehensive alternative detection failed:", error);

    // Final fallback to basic iframe detection
    const basicDetection = window.parent !== window.self;

    return {
      isTeams: basicDetection,
      method: "basic_fallback",
      confidence: basicDetection ? "low" : "high",
      error: error.message,
      timestamp: Date.now(),
    };
  }
};

/**
 * Quick Teams detection for immediate use (synchronous)
 */
export const isInTeamsQuick = () => {
  return (
    window.parent !== window.self ||
    window.name === "embedded-page-container" ||
    window.name === "extension-tab-frame" ||
    typeof window.nativeInterface !== "undefined" ||
    navigator.userAgent.toLowerCase().includes("teams") ||
    document.referrer.includes("teams.microsoft.com")
  );
};

/**
 * Get cached Teams detection result
 */
export const getCachedTeamsDetection = () => {
  try {
    const cached = sessionStorage.getItem("alternativeTeamsDetection");
    if (cached) {
      const parsed = JSON.parse(cached);
      const age = Date.now() - parsed.timestamp;

      if (age < 300000) {
        // 5 minutes cache
        console.log("📋 Using cached alternative Teams detection");
        return parsed;
      } else {
        sessionStorage.removeItem("alternativeTeamsDetection");
      }
    }
  } catch (error) {
    console.warn("⚠️ Failed to read cached detection:", error);
  }

  return null;
};

export default {
  detectTeamsEnvironmentAlternative,
  detectTeamsViaWindowAnalysis,
  detectTeamsViaUserAgent,
  detectTeamsViaDocument,
  detectTeamsViaStorage,
  detectTeamsViaGlobalObjects,
  detectTeamsViaNetwork,
  isInTeamsQuick,
  getCachedTeamsDetection,
};
